import React, { useState, useEffect } from "react";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";

export interface SmsTemplate {
  id: string;
  title: string;
  message: string;
  category: "bayram" | "duyuru" | "etkinlik" | "genel";
}

const COLLECTION = "smsTemplates";

const DEFAULT_TEMPLATES: SmsTemplate[] = [
  {
    id: "ramazan",
    title: "Ramazan Bayramı",
    category: "bayram",
    message: "Ramazan Bayraminizi en icten dileklerimizle kutluyor, saglik, huzur ve bereket dolu gunler diliyoruz. Bursa Trabzonlular Dernegi",
  },
  {
    id: "kurban",
    title: "Kurban Bayramı",
    category: "bayram",
    message: "Kurban Bayraminizi en icten dileklerimizle kutluyor, kurbaninizin ve ibadetinizin kabul olmasini diliyoruz. Bursa Trabzonlular Dernegi",
  },
  {
    id: "cumhuriyet",
    title: "29 Ekim Cumhuriyet Bayramı",
    category: "bayram",
    message: "Cumhuriyetimizin kurulusunun yil donumunde tum hemserilerimizin 29 Ekim Cumhuriyet Bayrami kutlu olsun. Bursa Trabzonlular Dernegi",
  },
  {
    id: "yilbasi",
    title: "Yeni Yıl",
    category: "bayram",
    message: "Yeni yilin saglik, mutluluk ve basarilar getirmesini diliyor, tum hemserilerimizin yeni yilini kutluyoruz. Bursa Trabzonlular Dernegi",
  },
  {
    id: "genel-kurul",
    title: "Genel Kurul Duyurusu",
    category: "duyuru",
    message: "Sayin uye, dernegimizin olaganustu genel kurulu ... tarihinde saat ...de ... adresinde yapilacaktir. Katiliminizi bekliyoruz. Bursa Trabzonlular Dernegi",
  },
  {
    id: "aidat",
    title: "Aidat Hatırlatma",
    category: "duyuru",
    message: "Sayin uye, ... yili uyelik aidatinizin odenmesini hatirlatir, katkilariniz icin tesekkur ederiz. Bursa Trabzonlular Dernegi",
  },
  {
    id: "etkinlik",
    title: "Etkinlik Duyurusu",
    category: "etkinlik",
    message: "Sayin uye, ... tarihinde saat ...de ... adresinde duzenlenecek etkinligimize davetlisiniz. Bilgi icin: ... Bursa Trabzonlular Dernegi",
  },
  {
    id: "hamsi",
    title: "Hamsi Günü Daveti",
    category: "etkinlik",
    message: "Sayin uye, geleneksel Hamsi Gunumuz ... tarihinde saat ...de ... adresinde yapilacaktir. Ailenizle birlikte bekliyoruz. Bursa Trabzonlular Dernegi",
  },
];

const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
  bayram: { label: "Bayram", color: "bg-green-100 text-green-700" },
  duyuru: { label: "Duyuru", color: "bg-blue-100 text-blue-700" },
  etkinlik: { label: "Etkinlik", color: "bg-purple-100 text-purple-700" },
  genel: { label: "Genel", color: "bg-gray-100 text-gray-600" },
};

interface Props {
  onUseTemplate: (msg: string) => void;
  onTabChange: (tab: string) => void;
}

const TemplatesPage: React.FC<Props> = ({ onUseTemplate, onTabChange }) => {
  // Firestore 'smsTemplates' koleksiyonu. Boşsa varsayılan şablonlar gösterilir
  // (otomatik Firestore'a yazılmaz; kullanıcı eklediğinde Firestore'a gider).
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("hepsi");
  const [editing, setEditing] = useState<SmsTemplate | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, COLLECTION));
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as SmsTemplate[];
      setTemplates(list);
    } catch (e) {
      console.error("Şablonlar okunamadı:", e);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Koleksiyon boşsa varsayılanları (salt-okunur) göster; doluysa Firestore'dakileri.
  const effectiveTemplates = templates.length > 0 ? templates : DEFAULT_TEMPLATES;
  const usingDefaults = templates.length === 0;

  const handleSave = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const payload = { title: editing.title, message: editing.message, category: editing.category };
      if (isNew || usingDefaults) {
        await addDoc(collection(db, COLLECTION), payload);
      } else {
        await updateDoc(doc(db, COLLECTION, editing.id), payload);
      }
      setEditing(null);
      setIsNew(false);
      await load();
    } catch (e) {
      console.error("Şablon kaydedilemedi:", e);
      window.alert("Şablon kaydedilemedi, lütfen tekrar deneyin.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (usingDefaults) return; // varsayılanlar Firestore'da değil, silinemez
    if (!window.confirm("Bu şablon silinsin mi?")) return;
    try {
      await deleteDoc(doc(db, COLLECTION, id));
      await load();
    } catch (e) {
      console.error("Şablon silinemedi:", e);
      window.alert("Şablon silinemedi.");
    }
  };

  // 8 varsayılan şablonu Firestore'a ekle. Mükerrer önleme: Firestore'da AYNI title
  // zaten varsa o şablon tekrar EKLENMEZ (butona iki kez basılırsa duplike olmaz).
  const [seeding, setSeeding] = useState(false);
  const seedDefaultTemplates = async () => {
    if (!window.confirm("8 hazır şablon (bayram, duyuru, etkinlik) eklenecek. Devam edilsin mi?")) return;
    setSeeding(true);
    try {
      const mevcutTitles = new Set(templates.map((t) => t.title.trim().toLowerCase()));
      const eksik = DEFAULT_TEMPLATES.filter((d) => !mevcutTitles.has(d.title.trim().toLowerCase()));
      await Promise.all(
        eksik.map((d) =>
          addDoc(collection(db, COLLECTION), { title: d.title, message: d.message, category: d.category })
        )
      );
      await load();
      window.alert(eksik.length > 0 ? `${eksik.length} yeni şablon eklendi.` : "Tüm varsayılan şablonlar zaten mevcut.");
    } catch (e) {
      console.error("Varsayılan şablonlar yüklenemedi:", e);
      window.alert("Şablonlar yüklenemedi, lütfen tekrar deneyin.");
    } finally {
      setSeeding(false);
    }
  };

  const handleUse = (msg: string) => {
    onUseTemplate(msg);
    onTabChange("sms");
  };

  const filtered = filter === "hepsi" ? effectiveTemplates : effectiveTemplates.filter(t => t.category === filter);

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">SMS Şablonları</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? "Yükleniyor..." : usingDefaults ? "Varsayılan şablonlar (düzenlemek için kaydedin)" : "Bayram, duyuru ve etkinlik için hazır mesaj şablonları"}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={seedDefaultTemplates}
            disabled={seeding}
            className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <i className={`fa-solid ${seeding ? "fa-circle-notch fa-spin" : "fa-rotate-left"}`} />
            {seeding ? "Yükleniyor..." : "Varsayılan Şablonları Yükle"}
          </button>
          <button
            onClick={() => { setIsNew(true); setEditing({ id: "", title: "", message: "", category: "genel" }); }}
            className="flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 rounded-lg"
            style={{ background: "linear-gradient(135deg, #8B1428, #0F4C81)" }}
          >
            <i className="fa-solid fa-plus" /> Yeni Şablon
          </button>
        </div>
      </div>

      {/* Filtre */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {["hepsi", "bayram", "duyuru", "etkinlik", "genel"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize transition-colors
              ${filter === f ? "bg-[#8B1428] text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            {f === "hepsi" ? "Hepsi" : CATEGORY_LABELS[f]?.label}
          </button>
        ))}
      </div>

      {/* Şablon kartları */}
      <div className="grid md:grid-cols-2 gap-4">
        {filtered.map(t => (
          <div key={t.id} className="bg-white rounded-2xl border border-gray-200 p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${CATEGORY_LABELS[t.category]?.color}`}>
                  {CATEGORY_LABELS[t.category]?.label}
                </span>
                <h3 className="font-bold text-gray-800 mt-1.5">{t.title}</h3>
              </div>
              {!usingDefaults && (
                <div className="flex gap-2 flex-shrink-0 ml-2">
                  <button onClick={() => { setEditing({ ...t }); setIsNew(false); }}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors">
                    <i className="fa-solid fa-pen text-xs" />
                  </button>
                  <button onClick={() => handleDelete(t.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-500 transition-colors">
                    <i className="fa-solid fa-trash text-xs" />
                  </button>
                </div>
              )}
            </div>

            <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-xl p-3 mb-4 min-h-[64px]">
              {t.message}
            </p>

            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">{t.message.length} karakter</span>
              <button onClick={() => handleUse(t.message)}
                className="flex items-center gap-2 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-opacity hover:opacity-90"
                style={{ background: "linear-gradient(135deg, #8B1428, #0F4C81)" }}>
                <i className="fa-solid fa-paper-plane" /> Bu Şablonu Kullan
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Düzenleme Modal */}
      {editing && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => { setEditing(null); setIsNew(false); }} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8">
              <h3 className="text-lg font-bold text-gray-800 mb-5">
                {isNew ? "Yeni Şablon" : "Şablonu Düzenle"}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Başlık</label>
                  <input type="text" value={editing.title}
                    onChange={e => setEditing(p => p && ({ ...p, title: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30"
                    placeholder="Şablon adı" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Kategori</label>
                  <select value={editing.category}
                    onChange={e => setEditing(p => p && ({ ...p, category: e.target.value as any }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30">
                    <option value="bayram">Bayram</option>
                    <option value="duyuru">Duyuru</option>
                    <option value="etkinlik">Etkinlik</option>
                    <option value="genel">Genel</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Mesaj</label>
                  <textarea rows={5} value={editing.message}
                    onChange={e => setEditing(p => p && ({ ...p, message: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#8B1428]/30"
                    placeholder="SMS mesajı..." />
                  <p className="text-xs text-gray-400 mt-1">{editing.message.length} karakter</p>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => { setEditing(null); setIsNew(false); }}
                  className="flex-1 border border-gray-200 text-gray-600 text-sm font-semibold py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                  İptal
                </button>
                <button onClick={handleSave}
                  disabled={!editing.title || !editing.message || busy}
                  className="flex-1 text-white text-sm font-semibold py-2.5 rounded-lg disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, #8B1428, #0F4C81)" }}>
                  {busy ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TemplatesPage;
